/**
 * DifferentiatorsGrid — editorial 3-card grid rendering the three
 * hand-seeded `degree.differentiators` lines. Feb 2026 redesign
 * spec. Each card carries a topical Lucide icon picked from the
 * text (placement → Briefcase, ranking → Award, data → BarChart3,
 * newsroom / media → Radio, accreditation → BadgeCheck, employer
 * / careers → TrendingUp, generic → Sparkles) and a subtle purple
 * accent bar on the left edge so the row reads as a proof cluster
 * rather than three neutral boxes.
 *
 * Renders nothing when the doc has fewer than 3 differentiators —
 * we don't fabricate.
 */
import {
  Award,
  BadgeCheck,
  BarChart3,
  Briefcase,
  Building2,
  GraduationCap,
  Radio,
  Sparkles,
  TrendingUp,
} from "lucide-react";

// Keyword → icon map. Order matters — first match wins.
const ICON_RULES = [
  [/\b(placement|internship|sandwich|work[- ]year)\b/i, Briefcase],
  [/\b(top\s?\d+|rank(ed|ing)?|league[- ]?table|prospects)\b/i, Award],
  [/\b(quantitative|data|analytic|statistic)\b/i, BarChart3],
  [/\b(newsroom|broadcast|BBC|studio|media)\b/i, Radio],
  [/\b(accredit|NCTJ|RIBA|RICS|ACCA|professional body|chartered)\b/i, BadgeCheck],
  [/\b(employ(er|ment)|graduate|salary|career|destination)\b/i, TrendingUp],
  [/\b(campus|hub|facility|building|square|centre|center)\b/i, Building2],
  [/\b(scholar|research|professor|academic)\b/i, GraduationCap],
];

function _pickIcon(line) {
  for (const [re, Icon] of ICON_RULES) {
    if (re.test(line)) return Icon;
  }
  return Sparkles;
}

export default function DifferentiatorsGrid({ items }) {
  const clean = Array.isArray(items) ? items.filter(Boolean) : [];
  if (clean.length < 3) return null;
  const shown = clean.slice(0, 3);
  return (
    <section
      data-testid="differentiators-list"
      className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
    >
      {shown.map((line, i) => {
        const Icon = _pickIcon(line);
        return (
          <div
            key={i}
            data-testid={`differentiator-item-${i}`}
            className="relative rounded-2xl border border-stone-200 bg-white/80 backdrop-blur-sm pl-5 pr-4 py-3.5 overflow-hidden"
          >
            {/* Left accent bar — brand purple, subtle. */}
            <span
              aria-hidden
              className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-[#4C1D95]/40"
            />
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-[#F5F0FF] text-[#4C1D95]"
                aria-hidden
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#4C1D95]">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="text-sm text-stone-800 leading-snug">
              {line}
            </div>
          </div>
        );
      })}
    </section>
  );
}
